import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AWNLogo from '../components/AWNLogo';
import { useAuth } from '../context/AuthContext';
import useAuthEmailCooldown from '../hooks/useAuthEmailCooldown';
import {
  logUserEvent,
  loginUser,
  normalizeEmail,
  resendVerificationEmail,
  signInWithGoogle,
} from '../services/authService';
import { isValidEmail } from '../utils/authValidation';
import styles from '../style/LoginPage.module.css';

const NOTICE_MESSAGES = {
  existing_account: 'Ihr Konto ist bereits vorhanden. Melden Sie sich jetzt an oder setzen Sie Ihr Passwort zurück.',
  password_reset: 'Ihr Passwort wurde geändert. Sie können sich jetzt mit dem neuen Passwort anmelden.',
};

function GoogleIcon() {
  return (
    <svg className={styles.socialIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function LoginPage() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => (
    typeof location.state?.email === 'string' ? location.state.email : ''
  ));
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [status, setStatus] = useState(() => {
    const message = NOTICE_MESSAGES[location.state?.notice];
    return message ? { type: 'info', message } : null;
  });
  const { secondsRemaining, startCooldown } = useAuthEmailCooldown();

  const normalizedEmail = normalizeEmail(email);
  const isUnconfirmed = status?.code === 'email_not_confirmed';

  useEffect(() => {
    if (!authLoading && user) navigate('/dashboard', { replace: true });
  }, [authLoading, user, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(null);

    if (!isValidEmail(email)) {
      setStatus({ type: 'error', message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' });
      return;
    }
    if (!password) {
      setStatus({ type: 'error', message: 'Bitte geben Sie Ihr Passwort ein.' });
      return;
    }

    setBusyAction('login');
    try {
      const data = await loginUser(normalizedEmail, password);
      await logUserEvent(data.user?.id, 'login');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setStatus({ type: 'error', code: error?.code, message: error.message });
    } finally {
      setBusyAction(null);
    }
  };

  const handleResendVerification = async () => {
    if (!isValidEmail(email) || secondsRemaining > 0) return;
    setBusyAction('resend');
    try {
      await resendVerificationEmail(normalizedEmail);
      setStatus({
        type: 'info',
        code: 'email_not_confirmed',
        message: 'Eine neue Bestätigungs-E-Mail wurde angefordert. Prüfen Sie bitte auch Spam und Werbung.',
      });
      startCooldown();
    } catch (error) {
      setStatus({ type: 'error', code: 'email_not_confirmed', message: error.message });
      if (error?.code?.includes('rate_limit')) startCooldown();
    } finally {
      setBusyAction(null);
    }
  };

  const handleGoogleLogin = async () => {
    setBusyAction('google');
    setStatus(null);
    try {
      await signInWithGoogle();
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Die Google-Anmeldung konnte nicht gestartet werden.' });
      setBusyAction(null);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.backgroundPattern} aria-hidden="true" />
      <div className={styles.splitContainer}>
        <aside className={styles.socialColumn}>
          <div className={styles.socialLogoSection}>
            <AWNLogo width={240} height={80} className={styles.companyLogo} />
          </div>
          <p className={styles.socialHint}>Nutzen Sie dieselbe Methode wie bei Ihrer Registrierung.</p>
          <div className={styles.socialButtonsStacked}>
            <button type="button" className={styles.googleButton} onClick={handleGoogleLogin} disabled={Boolean(busyAction)}>
              <GoogleIcon />
              <span>{busyAction === 'google' ? 'Google wird geöffnet…' : 'Mit Google fortfahren'}</span>
            </button>
          </div>
        </aside>

        <section className={styles.loginCard}>
          <div className={styles.logoSection}>
            <p className={styles.eyebrow}>Sicher anmelden</p>
            <h1 className={styles.welcomeTitle}>Willkommen zurück</h1>
            <p className={styles.welcomeSubtitle}>Melden Sie sich bei Ihrem AWN-Konto an.</p>
          </div>

          {status && (
            <div className={status.type === 'error' ? styles.errorText : styles.infoText} role={status.type === 'error' ? 'alert' : 'status'}>
              <span>{status.message}</span>
              {isUnconfirmed && (
                <button type="button" onClick={handleResendVerification} disabled={busyAction === 'resend' || secondsRemaining > 0} className={styles.resendBtn}>
                  {busyAction === 'resend' ? 'E-Mail wird angefordert…' : secondsRemaining > 0 ? `Erneut senden in ${secondsRemaining}s` : 'Bestätigungs-E-Mail erneut senden'}
                </button>
              )}
              {status.code === 'invalid_credentials' && (
                <Link to="/forgot-password" state={{ email: normalizedEmail }} className={styles.inlineLink}>Passwort zurücksetzen</Link>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.loginForm} noValidate>
            <div className={styles.inputGroup}>
              <label htmlFor="email">E-Mail-Adresse</label>
              <div className={styles.inputWrapper}>
                <input type="email" id="email" value={email} onChange={(event) => { setEmail(event.target.value); setStatus(null); }} autoComplete="username" inputMode="email" disabled={Boolean(busyAction)} />
                <span className={styles.inputIcon} aria-hidden="true">@</span>
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">Passwort</label>
              <div className={styles.inputWrapper}>
                <input type={showPassword ? 'text' : 'password'} id="password" value={password} onChange={(event) => { setPassword(event.target.value); setStatus(null); }} autoComplete="current-password" disabled={Boolean(busyAction)} />
                <span className={styles.inputIcon} aria-hidden="true">●</span>
                <button type="button" className={styles.passwordToggleIcon} onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'}>
                  {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                </button>
              </div>
            </div>

            <div className={styles.forgotRow}>
              <span className={styles.sessionHint}>Sitzung bleibt auf diesem Gerät gespeichert.</span>
              <Link to="/forgot-password" state={{ email: normalizedEmail }} className={styles.forgotLink}>Passwort vergessen?</Link>
            </div>

            <button type="submit" className={styles.submitButton} disabled={Boolean(busyAction)}>
              {busyAction === 'login' ? 'Anmeldung läuft…' : 'Anmelden'}
            </button>

            <div className={styles.registerSection}>
              <p>Noch kein Konto?</p>
              <Link to="/register" state={{ email: normalizedEmail }} className={styles.registerLink}>Konto erstellen</Link>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

export default LoginPage;
